/**
 * Token validation utilities for handling JWT token expiration
 */

interface JWTPayload {
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * Decode JWT token without verification (client-side only for expiration check)
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
};

/**
 * Check if a JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  if (!token) return true;
  
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return false; // If no expiration field, assume valid
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

/**
 * Get token expiration time in milliseconds
 */
export const getTokenExpiration = (token: string): number | null => {
  if (!token) return null;
  
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }
  
  return payload.exp * 1000; // Convert to milliseconds
};

/**
 * Check if token expires within the next N minutes
 */
export const isTokenExpiringSoon = (token: string, minutesThreshold: number = 5): boolean => {
  if (!token) return true;
  
  const expirationTime = getTokenExpiration(token);
  if (!expirationTime) {
    // If no expiration time, don't consider it expiring soon
    return false;
  }
  
  const thresholdTime = Date.now() + (minutesThreshold * 60 * 1000);
  return expirationTime < thresholdTime;
};

/**
 * Validate token and return validation result
 */
export const validateToken = (token: string): {
  isValid: boolean;
  isExpired: boolean;
  expiringSoon: boolean;
  expirationTime: number | null;
} => {
  const isExpired = isTokenExpired(token);
  const expiringSoon = isTokenExpiringSoon(token);
  const expirationTime = getTokenExpiration(token);
  
  const result = {
    isValid: !isExpired,
    isExpired,
    expiringSoon,
    expirationTime
  };
  
  return result;
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = (): void => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('sessionId');
  localStorage.removeItem('user');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userProfile');
}; 